import { QueryClient } from '@tanstack/react-query';

/**
 * ============================================================================
 * `retry: false` IS LOAD-BEARING — DO NOT CHANGE IT CASUALLY
 * ============================================================================
 *
 * TanStack Query defaults to `retry: 3`. Combined with a response interceptor that
 * toasts every rejection, that produces FOUR identical error toasts per failed
 * request. On the vendor-view screen, where ~30 dropdowns load, a backend blip would
 * bury the UI in toasts.
 *
 * Angular's `HttpClient` never retried, so `false` is also the parity-preserving
 * choice, not merely the safe one.
 *
 * `refetchOnWindowFocus: false` for the same reason — Angular never refetched on
 * focus, and enabling it would silently change how often the LAN backends are hit.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      /**
       * Errors surface as query state, not thrown. The error boundary is for render
       * crashes; HTTP failures are already toasted by the interceptor and are
       * rendered inline per screen.
       */
      throwOnError: false,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Reference data (countries, currencies, incoterms, payment terms…) does not change
 * mid-session. Spreading this into a dropdown query is where most of the
 * redundant-request saving lives: `vendor-view` alone fired 31 concurrent dropdown
 * loads on mount, many of them for lists shared with other screens.
 */
export const DROPDOWN_QUERY_DEFAULTS = {
  staleTime: 5 * 60_000,
  gcTime: 10 * 60_000,
} as const;
